import React, { useEffect, useState } from 'react'
import { DashboardService } from '../../../../Api/EmploymentEquity/Dashboard/DashboardService';
import { EmploymentEquityDashboardDto } from '../../../../Model/Dto/EmploymentEquity/Dashboard';
import { GenderList } from '../../../../Model/Enum/MultiSelectOptions/GenderOptions';
import { OccupationalLevelList } from '../../../../Model/Enum/MultiSelectOptions/OccupationallevelOptions';
import { RaceList } from '../../../../Model/Enum/MultiSelectOptions/RaceOptions';
import { mapIncomeDifferentialsToDoubleColumnChartData } from '../../../../Model/Mappers/EmploymentEquityMapper';
import { IMultiSelectOptions } from '../../MultiSelect/MultiSelect';
import { ChartFilter } from '../../Popovers/ChartFilter';
import { DoubleColumnChart } from '../Base/DoubleColumnChart/DoubleColumnChart';
import { AlertMessage } from '../../Alerts/alertMessage';

import { Box, ClickAwayListener, IconButton, Popper } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { MDBox } from '../../MDBox';
import { MDButton } from '../../MDButton';
import { RemunerationTypeList } from '../../../../Model/Enum/MultiSelectOptions/RemunerationTypeOptions';
import { EEScorecardAtom } from '../../../../../State/EEScorecard.Atom';
import { useRecoilValue } from 'recoil';
import { getDateWithoutTime, userFriendlyDate } from '../../../../../Helpers/DateFormatter';

const hash = require("object-hash");

interface IProps {
  scorecardId?: string;
  scorecardTypeId?: string;
  chartOptions?: any;
  dashboardLevelOnClick?: (chartLabel: string, occupationalLevelIds?: number[]) => void;
  sourceScorecardIds?: string[];
}

export const EmployementEquityIncomeDifferentialsChart = (props: IProps) => {
  const scorecard = useRecoilValue(EEScorecardAtom);
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [occupationLevelIds, setOccupationLevelIds] = useState(OccupationalLevelList());
  const [raceIds, setRaceIds] = useState(RaceList);
  const [genderIds, setGenderIds] = useState(GenderList);
  const [remunerationTypeIds, setRemunerationTypeIds] = useState(RemunerationTypeList);
  const [computedHash, setComputedHash] = useState('');
  const [isStacked, setIsStacked] = useState(props.chartOptions.isStacked ?? true);
  const [counter, setCounter] = useState(0);
  const [anchorElFilter, setAnchorElFilter] = React.useState<null | HTMLElement>(null);

  const [openFilter, setOpenFilter] = React.useState(false);

  useEffect(() => {
    const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
    getChartData(filterDate);
    setCounter(counter + 1);
  }, [props.sourceScorecardIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFilter(anchorElFilter ? null : event.currentTarget);
    setOpenFilter(true);
  };

  const onChartSegmentClick = (finalLabel: string) => {
    if (props.dashboardLevelOnClick) {
      const selectedOccupationalLevelIds = occupationLevelIds.map((item) => (parseInt(item.value)));
      props.dashboardLevelOnClick(finalLabel, selectedOccupationalLevelIds);
      return;
    }
  }

  const changeStackedView = () => {
    setIsStacked(isStacked ? false : true);
  }

  const updateGenderFilter = (selectedGenderFilterOptions: Array<IMultiSelectOptions>) => {
    setGenderIds(selectedGenderFilterOptions);
    const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
    getChartData(filterDate);
    setCounter(counter + 1);
  }

  const updateRaceFilter = (selectedRaceFilterOptions: Array<IMultiSelectOptions>) => {
    setRaceIds(selectedRaceFilterOptions);
    const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
    getChartData(filterDate);
    setCounter(counter + 1);
  }

  const updateOccupationLevelFilter = (selectedOccupationLevelFilterOptions: Array<IMultiSelectOptions>) => {
    setOccupationLevelIds(selectedOccupationLevelFilterOptions);
    const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
    getChartData(filterDate);
    setCounter(counter + 1);
  }

  const updateRemunerationTypeFilter = (selectedRemunerationTypeFilterOptions: Array<IMultiSelectOptions>) => {
    setRemunerationTypeIds(selectedRemunerationTypeFilterOptions);
    const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
    getChartData(filterDate);
    setCounter(counter + 1);
  }

  const resetChartFilters = () => {
    setOccupationLevelIds(OccupationalLevelList());
    setRaceIds(RaceList);
    setGenderIds(GenderList);
    setRemunerationTypeIds(RemunerationTypeList);
    const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
    getChartData(filterDate);
    setAnchorElFilter(null);
    setOpenFilter(false);
    setCounter(counter + 1);
  }

  const closeChartFilter = () => {
    setAnchorElFilter(null);
    setOpenFilter(false);
  }

  const getChartData = (filteredDate: Date) => {
    let SelectedGenderArray: Array<number> = [];
    let SelectedRaceArray: Array<number> = [];
    let SelectedOccupationalLevelArray: Array<number> = [];
    let SelectedRemunerationTypeArray: Array<number> = [];
    genderIds.map((item) => SelectedGenderArray.push(parseInt(item.value)));
    raceIds.map((item) => SelectedRaceArray.push(parseInt(item.value)));
    occupationLevelIds.map((item) => SelectedOccupationalLevelArray.push(parseInt(item.value)));
    remunerationTypeIds.map((item) => SelectedRemunerationTypeArray.push(parseInt(item.value)));
    const data: EmploymentEquityDashboardDto.IncomeDifferentialsRequest = {
      occupationLevelIds: SelectedOccupationalLevelArray,
      raceIds: SelectedRaceArray,
      genderIds: SelectedGenderArray,
      remunerationTypeIds: SelectedRemunerationTypeArray,
      filterDate: filteredDate,
      scorecardId: props.scorecardId,
      sourceScorecardIds: props.sourceScorecardIds ? props.sourceScorecardIds : undefined
    }
    setIsStacked(false);
    return DashboardService.GetIncomeDifferentials(data).then(
      mapIncomeDifferentialsToDoubleColumnChartData(remunerationTypeIds, props.chartOptions.isPowerPointPrint)
    );
  }

  const renderFilters = () => {
    return <MDBox style={{ float: 'right' }}>
      <IconButton
        color="primary"
        onClick={filterClick} >
        <FilterAltIcon />
      </IconButton>
      {openFilter && <ClickAwayListener onClickAway={() => closeChartFilter()}>
        <Popper
          open={openFilter}
          anchorEl={anchorElFilter}
          placement="bottom-end"
        >
          <Box sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            borderRadius: 3,
            p: 2,
            border: '1px solid rgba(0, 0, 0, 0.25)',
            maxWidth: 530,
            minWidth: 400
          }}>
            <ChartFilter
              resetChartFilters={() => { resetChartFilters() }}
              enableResetFilter={true}
              dropdowns={[
                {
                  label: "Gender",
                  options: GenderList,
                  selected: genderIds,
                  onChange: updateGenderFilter,
                },
                {
                  label: "Race",
                  options: RaceList,
                  selected: raceIds,
                  onChange: updateRaceFilter,
                },
                {
                  label: "Occupational Level",
                  options: OccupationalLevelList(),
                  selected: occupationLevelIds,
                  onChange: updateOccupationLevelFilter,
                },
                {
                  label: "Type",
                  options: RemunerationTypeList,
                  selected: remunerationTypeIds,
                  onChange: updateRemunerationTypeFilter,
                },
              ]}
            ></ChartFilter>
          </Box>
        </Popper>
      </ClickAwayListener>
      }
    </MDBox>
  }

  const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
  const newHash = computedHash + filterDate?.toISOString();
  return (
    <>
      <MDBox>
        <MDBox sx={{ margin: 1 }}>
          {renderFilters()}
          <MDButton color='primary' style={{ float: 'right' }}
            onClick={() => { changeStackedView() }}
          >
            {isStacked ? "Column View" : "Stacked View"}
          </MDButton>
          <br />
          <br />
          {
            genderIds.length > 0 &&
            raceIds.length > 0 &&
            occupationLevelIds.length > 0 &&
            remunerationTypeIds.length > 0 && (
              <DoubleColumnChart
                key={counter}
                hash={newHash}
                chartOptions={props.chartOptions}
                fetchData={() => getChartData(filterDate)}
                onSegmentClick={onChartSegmentClick}
                isStacked={isStacked}
              />
            )
          }
          {
            props.chartOptions?.filterDate != null &&
            <span className="bottom-of-chart inline-block">For the measurement period <b>{userFriendlyDate(scorecard.ReportPeriodDateFrom)}</b> to <b>{userFriendlyDate(scorecard.ReportPeriodDateTo)}</b></span>
          }
          <br />
          <span><small><i>Note: Salary being displayed is the fixed plus variable salary annualized</i></small></span>
          {
            genderIds.length === 0 && (
              <AlertMessage
                type="danger"
                message="Please select atleast one Gender"
              />
            )}
          {raceIds.length === 0 && (
            <AlertMessage
              type="danger"
              message="Please select atleast one Race"
            />
          )
          }
          {
            occupationLevelIds.length === 0 && (
              <AlertMessage
                type="danger"
                message="Please select atleast one Occupational Level"
              />
            )
          }
          {
            remunerationTypeIds.length === 0 && (
              <AlertMessage
                type="danger"
                message="Please select atleast one Type"
              />
            )
          }
        </MDBox>
      </MDBox>
    </>
  )
}

